import { useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';

import ConfigCollectionSelect from './ConfigCollectionSelect';
import { collectionApis } from '../../../apis/collectionApis';
import useToast, { TOAST_TYPE } from '../../../hooks/useToast';
import { isSolrCloud, sentenceCase } from '../../../utils/utils';
import AlertMessage from '../../elements/alert-message/AlertMessage';
import Loading from '../../elements/loading/Loading';
import FileManager from '../file-manager/FileManager';

const BASE_FOLDER_CHAIN = [{ id: 'base_file', name: 'conf', isDir: true }];

const ConfigContent = ({ collections, instanceType }) => {
  const location = useLocation();
  const { projectId, instanceId } = useParams();
  const { showToast } = useToast();
  const { t } = useTranslation();

  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [homePath, setHomePath] = useState('');
  // If the user returns from the editing page we want to use the previously selected collection
  // Otherwise we can just set it to the first collection on the list.
  const [selectedCollection, setSelectedCollection] = useState(
    location.state?.collection || collections[0]
  );

  const onCollectionChange = (name) => setSelectedCollection(name);

  const getHomePath = async () => {
    setLoading(true);
    setError(false);
    try {
      const solrCloud = isSolrCloud(instanceType);

      const { data: pathRes } = await collectionApis.getCollectionRootPath(
        projectId,
        instanceId,
        { name: selectedCollection, solrCloud }
      );
      // Because the base chain adds the '/conf' endpoint, we manually remove it here.
      // This is so the folder breadcrumbs display correctly for the user
      const formattedPath = pathRes.rootPath.slice(0, -5);
      setHomePath(formattedPath);
    } catch (error) {
      setError(true);
      showToast(TOAST_TYPE.ERROR, t('filePath', { ns: 'errors' }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getHomePath();
  }, [selectedCollection]);

  if (error)
    return (
      <AlertMessage
        severity="error"
        action={getHomePath}
        message={sentenceCase(
          t('loading', {
            ns: 'errors',
            content: t('requestedFileList', { ns: 'instance' }),
          })
        )}
      />
    );

  return (
    <>
      <ConfigCollectionSelect
        instanceType={instanceType}
        collections={collections}
        onCollectionChange={onCollectionChange}
        selectedCollection={selectedCollection}
      />

      {loading ? (
        <Loading />
      ) : (
        <FileManager
          baseFolderChain={BASE_FOLDER_CHAIN}
          basePath={homePath}
          collection={selectedCollection}
        />
      )}
    </>
  );
};

ConfigContent.propTypes = {
  collections: PropTypes.array.isRequired,
  instanceType: PropTypes.string.isRequired,
};

export default ConfigContent;
